import { useQuery, useUserInfo, useGameConfig, usePromotionClient, useInGameMessages, useClock, useDayjs, computed } from '#imports'

/**
 * Composable to fetch and check promotion game slugs
 *
 * Fetches game slugs that have active player bonuses from the API.
 * Only fetches when the user is logged in.
 *
 * @returns Object containing promotion game data and utility functions
 *
 * @example
 * const { gameHasSpins, gamesWithSpins } = usePromotionGames()
 * const hasPromotion = gameHasSpins('some-game-slug')
 */
export const usePromotionGames = () => {
    const { canUsePlayerFeatures } = useUserInfo()
    const dayjs = useDayjs()
    const { clock } = useClock()

    const { gamePromotions } = useGameConfig()
    const { forGame: inGameMessagesFor } = useInGameMessages()

    const { client } = usePromotionClient()

    const query = useQuery(async ({ signal }) => {
        const { data } = await client.GET('/public/player-bonuses/active-with-game-slugs', { signal })

        return data || { spins: [], bingoTickets: [] }
    }, {
        key: 'game:promotionGames',
        enabled: canUsePlayerFeatures,
        server: false,
        lazy: true
    })

    const gamesWithSpins = computed(() => query.data.value?.spins || [])
    const gamesWithBingoTickets = computed(() => query.data.value?.bingoTickets || [])

    const gameHasSpins = (gameSlug: string) => {
        return gamesWithSpins.value.includes(gameSlug)
    }

    const gameHasTickets = (gameSlug: string) => {
        return gamesWithBingoTickets.value.includes(gameSlug)
    }

    // One tz() per clock value for every game in the list: tz() is slow, and SSR does not cache computeds.
    let reading: { clock: string, now: ReturnType<typeof dayjs> } | undefined

    function now() {
        if (reading?.clock !== clock.value) reading = { clock: clock.value, now: dayjs(clock.value).tz() }

        return reading.now
    }

    const gameHasInGameMessage = (gameSlug: string): boolean => {
        return inGameMessagesFor(gameSlug, now().valueOf()).length > 0
    }

    const gameHasPromotion = (gameSlug: string) => {
        if (!gamePromotions) return false

        return gamePromotions.some(gamePromotion =>
            gamePromotion.slugs.includes(gameSlug)
            && now().isBetween(gamePromotion.startDate, gamePromotion.endDate)
        )
    }

    return query.enrich({
        gamesWithSpins,
        gamesWithBingoTickets,
        gameHasSpins,
        gameHasTickets,
        gameHasPromotion,
        gameHasInGameMessage,
    })
}
